import React, { Fragment, useState } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Divider,
  Stack,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Autocomplete,
  Alert,
  Skeleton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { notificationService } from "../../services/api";
import SubPageLayout from "../../components/SubPageLayout";

/*
Python's ZoneInfo uses a IANA timezone name (e.g. "America/Sao_Paulo") as an
argument. We don't need to show the user every available timezone, just one
representative per offset. This is a simple way to get a list of representative
timezones, sorted by offset. This approach does not account for DST, but it's good
enough for our use case. DST should be handled (TODO) by the backend when the user saves their settings, and when sending notifications.
*/
const gmtOffset = (tz) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" })
    .formatToParts()
    .find((part) => part.type === "timeZoneName").value;

const offsetMinutes = (label) => {
  const [, sign, hours, minutes] = /GMT([+-])(\d\d):(\d\d)/.exec(label) || [];
  return sign ? (sign === "-" ? -1 : 1) * (+hours * 60 + +minutes) : 0;
};

const TIMEZONE_LABELS = new Map(
  (Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : []).map(
    (tz) => [tz, gmtOffset(tz)],
  ),
);

const offsetOf = (tz) => TIMEZONE_LABELS.get(tz) || gmtOffset(tz);

const TIMEZONES = (() => {
  const representative = new Map();
  for (const tz of [...TIMEZONE_LABELS.keys()].sort())
    if (!representative.has(offsetOf(tz))) representative.set(offsetOf(tz), tz);
  return [...representative.values()].sort(
    (a, b) => offsetMinutes(offsetOf(a)) - offsetMinutes(offsetOf(b)),
  );
})();

const CHANNELS = [
  {
    key: "push_enabled",
    label: "Push",
    hintKey: "notifications.hint_push",
    enabled: true,
  },
  {
    key: "email_enabled",
    label: "E-mail",
    hintKey: "notifications.hint_email",
    enabled: false,
  },
  {
    key: "sms_enabled",
    label: "SMS",
    hintKey: "notifications.hint_sms",
    enabled: false,
  },
  {
    key: "whatsapp_enabled",
    label: "WhatsApp",
    hintKey: "notifications.hint_whatsapp",
    enabled: false,
  },
];

const DEFAULTS = {
  push_enabled: false,
  email_enabled: false,
  sms_enabled: false,
  whatsapp_enabled: false,
  dnd_start: "21:00",
  dnd_end: "06:00",
  timezone: "America/Sao_Paulo",
  enabled_events: {
    "push.message.ready": true,
    "push.lead.created": true,
    "push.lead.transferred": true,
    "push.lead.handoff": true,
  },
};

// Catalog keys are "{type}.{event}" — the type is everything before the first period.
const typeOf = (catalogKey) => catalogKey.slice(0, catalogKey.indexOf("."));
const eventOf = (catalogKey) => catalogKey.slice(catalogKey.indexOf(".") + 1);

const NotificationSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState(null);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const { data: notificationSettings, isLoading } = useQuery({
    queryKey: ["notificationSettings"],
    queryFn: () =>
      notificationService.getNotificationSettings().then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: ({ id, data }) =>
      notificationService.updateNotificationSettings(id, data),
    onSuccess: (response) => {
      queryClient.setQueryData(["notificationSettings"], response.data.data);
    },
    onError: () => {
      setError(t("notifications.error"));
    },
  });

  // Server value until the user touches a field, then their in-progress edits.
  const settings = edits ?? notificationSettings ?? DEFAULTS;

  const dndEnabled = settings.dnd_enabled;

  const save = (next) => {
    setEdits(next);
    if (!notificationSettings?.uuid) {
      setError(t("notifications.error_no_settings"));
      return;
    }
    if (next.dnd_start !== null && !(next.dnd_start && next.dnd_end)) return;
    setError("");
    saveMutation.mutate({ id: notificationSettings.uuid, data: next });
  };

  const set = (key, value) => save({ ...settings, [key]: value });

  const toggleDnd = (checked) =>
    save({
      ...settings,
      dnd_enabled: checked,
    });

  return (
    <SubPageLayout
      title={t("notifications.title")}
      onBack={() => navigate(location.state?.from || "/settings")}
    >
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={250} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={250} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={230} sx={{ borderRadius: 2 }} />
          </>
        ) : (
          <>
            <Card>
              <CardHeader
                title={t("notifications.types")}
                subheader={t("notifications.types_subtitle")}
                titleTypographyProps={{ variant: "h6" }}
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                <List disablePadding>
                  {CHANNELS.map((channel, i) => {
                    const on = channel.enabled && !!settings[channel.key];
                    const isLastChannel = i === CHANNELS.length - 1;
                    const events = Object.keys(
                      settings.enabled_events || {},
                    ).filter(
                      (key) =>
                        typeOf(key) === channel.key.replace("_enabled", ""),
                    );
                    const expanded = on && events.length > 0;
                    return (
                      <Fragment key={channel.key}>
                        <ListItem
                          disablePadding
                          divider={!expanded && !isLastChannel}
                          secondaryAction={
                            <Switch
                              edge="end"
                              checked={on}
                              onChange={(e) =>
                                set(channel.key, e.target.checked)
                              }
                              disabled={!channel.enabled}
                              inputProps={{ "aria-label": channel.label }}
                            />
                          }
                        >
                          <ListItemButton
                            disabled={!channel.enabled}
                            onClick={() => set(channel.key, !on)}
                          >
                            <ListItemText
                              primary={channel.label}
                              secondary={t(channel.hintKey)}
                            />
                          </ListItemButton>
                        </ListItem>
                        <Collapse
                          in={expanded}
                          timeout="auto"
                          unmountOnExit
                          component="li"
                        >
                          <List disablePadding component="div">
                            {events.map((key, j) => (
                              <ListItem
                                key={key}
                                disablePadding
                                divider={j === events.length - 1 && !isLastChannel}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    size="small"
                                    checked={!!settings.enabled_events[key]}
                                    onChange={(e) =>
                                      set("enabled_events", {
                                        ...settings.enabled_events,
                                        [key]: e.target.checked,
                                      })
                                    }
                                    inputProps={{
                                      "aria-label": t(
                                        `notifications.events.${eventOf(key)}`,
                                      ),
                                    }}
                                  />
                                }
                              >
                                <ListItemButton
                                  sx={{ pl: 4 }}
                                  onClick={() =>
                                    set("enabled_events", {
                                      ...settings.enabled_events,
                                      [key]: !settings.enabled_events[key],
                                    })
                                  }
                                >
                                  <ListItemText
                                    primary={t(
                                      `notifications.events.${eventOf(key)}`,
                                    )}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </Fragment>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
            <Card>
              <CardHeader
                title={t("notifications.dnd")}
                subheader={t("notifications.dnd_subtitle")}
                titleTypographyProps={{ variant: "h6" }}
                action={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ pt: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        dndEnabled
                          ? "notifications.dnd_enabled"
                          : "notifications.dnd_disabled",
                      )}
                    </Typography>
                    <Switch
                      checked={dndEnabled}
                      onChange={(e) => toggleDnd(e.target.checked)}
                      inputProps={{ "aria-label": t("notifications.dnd") }}
                    />
                  </Stack>
                }
              />
              <Divider />
              <CardContent
                sx={{
                  opacity: dndEnabled ? 1 : 0.5,
                  transition: "opacity .2s",
                }}
              >
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label={t("notifications.dnd_start")}
                      type="time"
                      fullWidth
                      disabled={!dndEnabled}
                      value={(settings.dnd_start || "").slice(0, 5)}
                      onChange={(e) => set("dnd_start", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      slotProps={{ htmlInput: { step: 300 } }}
                    />
                    <TextField
                      label={t("notifications.dnd_end")}
                      type="time"
                      fullWidth
                      disabled={!dndEnabled}
                      value={(settings.dnd_end || "").slice(0, 5)}
                      onChange={(e) => set("dnd_end", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      slotProps={{ htmlInput: { step: 300 } }}
                    />
                    <Autocomplete
                      fullWidth
                      disabled={!dndEnabled}
                      options={TIMEZONES}
                      getOptionLabel={offsetOf}
                      isOptionEqualToValue={(a, b) =>
                        offsetOf(a) === offsetOf(b)
                      }
                      value={settings.timezone}
                      onChange={(_, value) => set("timezone", value)}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("notifications.timezone")}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Stack>

                  <Alert severity="info">
                    {dndEnabled
                      ? t("notifications.dnd_info", {
                          start: (settings.dnd_start || "").slice(0, 5),
                          end: (settings.dnd_end || "").slice(0, 5),
                          tz: offsetOf(settings.timezone),
                        })
                      : t("notifications.dnd_info_disabled")}
                  </Alert>
                </Stack>
              </CardContent>
            </Card>

            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
              color="text.secondary"
            >
              <CheckIcon fontSize="small" />
              <Typography variant="body2">
                {t("notifications.footer")}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </SubPageLayout>
  );
};

export default NotificationSettings;
